import gsap from "gsap";
import { SplitText } from "gsap/src/SplitText";
import { ScrollTrigger } from "gsap/src/ScrollTrigger";
import Lenis from "lenis";


gsap.registerPlugin(SplitText, ScrollTrigger);

// Keep a single root GSAP context so we can revert everything on re-init / transitions
let rootCtx = null;
let cleanupPlusCursor = null;
let cleanupDrawerReveal = null;
let textRevealSplits = [];
let hasGalleryPreviewToggleListener = false;

// Cache reduced-motion preference once
const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

// Global coarse-pointer / touch detector
const isTouchDevice =
  (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) ||
  'ontouchstart' in window ||
  navigator.maxTouchPoints > 0;

// ✅ Conditional Lenis initialization: Disable on Safari or Stage mode
const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
const isStageMode = new URLSearchParams(window.location.search).get('stage') === 'Stage';
const lenis = (isSafari || isStageMode) ? null : new Lenis({
  duration: 1.5,
  easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
  smoothWheel: true,
  smoothTouch: false
});

// ✅ Export Lenis instance for global access
export { lenis };

// Animation frame loop to sync Lenis with GSAP
function raf(time) {
  if (lenis) lenis.raf(time);
  requestAnimationFrame(raf);
}
requestAnimationFrame(raf);

// ✅ Fix: Ensure Lenis updates when the page loads and lazy images load
let resizeTimeout;

function refreshLenis() {
  clearTimeout(resizeTimeout); // Cancel previous calls
  resizeTimeout = setTimeout(() => {
    requestAnimationFrame(() => {
      if (lenis) lenis.resize(); // ✅ Recalculate dimensions smoothly
    });
  }, 3000); // Adjust delay if necessary
}

// ✅ Refresh Lenis when the page fully loads (not on Safari or Stage mode)
if (!isSafari && !isStageMode) {
  window.addEventListener("load", refreshLenis);
}



function createParallaxEffect() {
  // Don't run parallax if in editing/highlight mode
  if (document.body.classList.contains('highlight-editable-fields')) {
    return;
  }

  gsap.utils.toArray('.parallax-img').forEach(container => {
    const img = container.querySelector('img');
    if (!img) return;

    // Avoid re-initialising if your site transitions/re-inits scripts
    if (container.dataset.parallaxInit === '1') return;
    container.dataset.parallaxInit = '1';

    // Minimal style writes
    container.style.position = container.style.position || 'relative';
    container.style.overflow = container.style.overflow || 'hidden';

    gsap.set(img, {
      scale: 1.082,
      transformOrigin: 'center center',
      willChange: prefersReducedMotion ? 'auto' : 'transform'
    });

    gsap.fromTo(
      img,
      { yPercent: -6 },
      {
        yPercent: 6,
        ease: 'none',
        scrollTrigger: {
          trigger: container,
          scrub: prefersReducedMotion ? 1 : 0.3,
          invalidateOnRefresh: true
        }
      }
    );
  });
}



function pinSections() {
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  gsap.utils.toArray('.pinned').forEach(section => {
    const isPhoneDisabled = section.classList.contains('disable-phone');
    if (isTouchDevice && isPhoneDisabled) return;

    gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: "top top",
        end: "bottom top",
        pin: true,
        pinSpacing: false,
        scrub: true
      }
    });
  });
}

function ParallaxContent() {
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  if (isTouchDevice) return;

  gsap.utils.toArray('.parallax-content').forEach(el => {
    const speed = el.dataset.speed ? parseFloat(el.dataset.speed) : -20;

    gsap.to(el, {
      yPercent: speed,
      ease: 'none',
      scrollTrigger: {
        trigger: el,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true
      }
    });
  });
}



function linkHoverEffect() {
  document
    .querySelectorAll('.html_content a, footer address a, footer ul a, footer p a, .ani-link a')
    .forEach(link => {
      if (link.dataset.hoverInit === '1') return;
      link.dataset.hoverInit = '1';

      // Ensure positioning only once
      if (getComputedStyle(link).position === 'static') {
        link.style.position = 'relative';
      }

      // Prevent duplicate underline spans if scripts re-init
      let underline = link.querySelector(':scope > span.gn-underline');
      if (!underline) {
        underline = document.createElement('span');
        underline.className = 'gn-underline';
        underline.style.cssText = `
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 100%;
          height: 1px;
          background: currentColor;
          transform: scaleX(0);
          transform-origin: left;
          pointer-events: none;
          will-change: transform;
        `;
        link.appendChild(underline);
      }

      link.addEventListener('mouseenter', () => {
        gsap.killTweensOf(underline);
        gsap.to(link, { opacity: 0.8, duration: 0.2, ease: 'power1.out', overwrite: 'auto' });
        gsap.to(underline, { scaleX: 1, duration: 0.35, ease: 'power2.out', overwrite: 'auto' });
      }, { passive: true });

      link.addEventListener('mouseleave', () => {
        gsap.killTweensOf(underline);
        gsap.to(link, { opacity: 1, duration: 0.3, ease: 'power1.out', overwrite: 'auto' });
        gsap.to(underline, { scaleX: 0, duration: 0.25, ease: 'power1.out', overwrite: 'auto' });
      }, { passive: true });
    });
}

function fadeUpAnimation() {
  const elements = gsap.utils.toArray('.fade-up');
  if (!elements.length) return;

  function isComposerEditMode(el) {
    const gallery = el?.closest?.('#project_gallery');
    if (!gallery) return false;
    const hasComposer = !!gallery.querySelector('#galleryLayoutInspector');
    if (!hasComposer) return false;
    return !gallery.classList.contains('editor-preview-mode');
  }

  function clearFadeUpForEdit(el) {
    if (!el) return;
    if (el._fadeUpTrigger) {
      el._fadeUpTrigger.kill();
      el._fadeUpTrigger = null;
    }
    gsap.killTweensOf(el);
    delete el.dataset.fadeUpInit;
    gsap.set(el, {
      autoAlpha: 1,
      y: 0,
      clearProps: 'transform,willChange'
    });
  }

  function initFadeUpForElement(el) {
    if (!el || el.dataset.fadeUpInit === '1') return;
    if (isComposerEditMode(el)) {
      clearFadeUpForEdit(el);
      return;
    }

    el.dataset.fadeUpInit = '1';
    const yOffset = parseFloat(el.getAttribute('data-fade-up-y')) || 22;
    const duration = parseFloat(el.getAttribute('data-fade-up-duration')) || 0.78;
    const delay = parseFloat(el.getAttribute('data-fade-up-delay')) || 0;
    const start = el.getAttribute('data-fade-up-start') || 'top 87%';

    // Reduced-motion users get immediate visibility with no transform cost.
    if (prefersReducedMotion) {
      gsap.set(el, { autoAlpha: 1, y: 0, clearProps: 'willChange' });
      return;
    }

    // Keep pre-state lightweight and deterministic for each block.
    gsap.set(el, {
      autoAlpha: 0,
      y: yOffset,
      force3D: true,
      willChange: 'transform, opacity'
    });

    const tween = gsap.to(el, {
      autoAlpha: 1,
      y: 0,
      delay,
      duration,
      ease: 'power3.out',
      overwrite: 'auto',
      scrollTrigger: {
        trigger: el,
        start,
        once: true,
        fastScrollEnd: true,
        invalidateOnRefresh: true
      },
      onComplete: () => {
        // Drop render hints once done to keep long pages cheap.
        el.style.willChange = 'auto';
      }
    });

    el._fadeUpTrigger = tween?.scrollTrigger || null;
  }

  elements.forEach((el) => {
    initFadeUpForElement(el);
  });

  if (!hasGalleryPreviewToggleListener) {
    hasGalleryPreviewToggleListener = true;
    window.addEventListener('loci:gallery-preview-toggle', (event) => {
      const isPreview = !!event?.detail?.preview;
      const galleryElements = gsap.utils.toArray('#project_gallery .fade-up');
      if (!galleryElements.length) return;

      galleryElements.forEach((el) => {
        if (isPreview) {
          initFadeUpForElement(el);
          return;
        }
        clearFadeUpForEdit(el);
      });

      ScrollTrigger.refresh();
    });
  }
}

function drawerMenuRevealAnimation() {
  cleanupDrawerReveal?.();

  const openBtn = document.querySelector('[data-drawer-open]');
  const closeBtn = document.querySelector('[data-drawer-close]');
  const panel = document.querySelector('[data-drawer-panel]');
  const backdrop = document.querySelector('[data-drawer-backdrop]');
  const items = gsap.utils.toArray('[data-drawer-item]');
  const closeLinks = gsap.utils.toArray('[data-drawer-link]');
  const searchToggle = document.querySelector('[data-drawer-search-toggle]');
  const searchPanel = document.querySelector('[data-drawer-search-panel]');
  const searchInput = document.querySelector('[data-drawer-search-input]');
  if (!openBtn || !closeBtn || !panel || !backdrop || !items.length) return;

  let openTl = null;
  let closeTl = null;
  let searchTl = null;
  let isOpen = panel.hidden === false;
  let isSearchOpen = false;
  let lastFocus = null;

  const setClosedVisualState = () => {
    gsap.set(backdrop, { autoAlpha: 0, pointerEvents: 'none' });
    gsap.set(panel, { xPercent: -100, pointerEvents: 'none' });
    gsap.set(items, { autoAlpha: 0, y: 10 });
  };

  const resetSearch = () => {
    if (!searchToggle || !searchPanel) return;
    searchTl?.kill();
    isSearchOpen = false;
    searchToggle.setAttribute('aria-expanded', 'false');
    if (searchInput) searchInput.value = '';
    gsap.set(searchToggle, {
      autoAlpha: 1,
      width: 'auto',
      pointerEvents: 'auto',
      clearProps: 'transform,opacity,visibility'
    });
    gsap.set(searchPanel, { width: 0, autoAlpha: 0, pointerEvents: 'none' });
  };

  const setOpenVisualState = () => {
    gsap.set(backdrop, { autoAlpha: 1, pointerEvents: 'auto' });
    gsap.set(panel, { xPercent: 0, pointerEvents: 'auto' });
    gsap.set(items, { autoAlpha: 1, y: 0, clearProps: 'transform,opacity,visibility' });
  };

  const completeClose = () => {
    panel.hidden = true;
    backdrop.hidden = true;
    document.body.classList.remove('overflow-hidden');
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
  };

  const openDrawer = () => {
    if (isOpen) return;
    isOpen = true;
    lastFocus = document.activeElement;
    panel.hidden = false;
    backdrop.hidden = false;
    document.body.classList.add('overflow-hidden');
    openBtn.setAttribute('aria-expanded', 'true');

    gsap.killTweensOf(items);
    gsap.killTweensOf(panel);
    gsap.killTweensOf(backdrop);
    openTl?.kill();
    closeTl?.kill();
    resetSearch();

    if (prefersReducedMotion) {
      setOpenVisualState();
      return;
    }

    gsap.set(backdrop, { autoAlpha: 0, pointerEvents: 'auto' });
    gsap.set(panel, { xPercent: -100, pointerEvents: 'auto' });
    gsap.set(items, { autoAlpha: 0, y: 10 });

    openTl = gsap.timeline({ defaults: { overwrite: 'auto' } });
    openTl
      .to(backdrop, {
        autoAlpha: 1,
        duration: 0.28,
        ease: 'power1.out'
      }, 0)
      .to(panel, {
        xPercent: 0,
        duration: 0.46,
        ease: 'power3.out'
      }, 0)
      .to(items, {
        autoAlpha: 1,
        y: 0,
        duration: 0.42,
        ease: 'power2.out',
        stagger: 0.04,
        clearProps: 'transform,opacity,visibility'
      }, 0.1);

    closeBtn.focus();
  };

  const closeDrawer = () => {
    if (!isOpen) return;
    isOpen = false;
    openBtn.setAttribute('aria-expanded', 'false');

    gsap.killTweensOf(items);
    gsap.killTweensOf(panel);
    gsap.killTweensOf(backdrop);
    openTl?.kill();
    closeTl?.kill();
    resetSearch();

    const onClosedComplete = () => {
      setClosedVisualState();
      completeClose();
    };

    if (prefersReducedMotion) {
      onClosedComplete();
      return;
    }

    closeTl = gsap.timeline({
      defaults: { overwrite: 'auto' },
      onComplete: onClosedComplete
    });

    closeTl
      .to(items, {
        autoAlpha: 0,
        y: 8,
        duration: 0.2,
        ease: 'power1.in',
        stagger: { each: 0.025, from: 'end' }
      }, 0)
      .to(panel, {
        xPercent: -100,
        duration: 0.38,
        ease: 'power3.inOut'
      }, 0.04)
      .to(backdrop, {
        autoAlpha: 0,
        duration: 0.24,
        ease: 'power1.in'
      }, 0.12);
  };

  const onKeydown = (event) => {
    if (!isOpen) return;
    if (event.key === 'Escape') {
      if (isSearchOpen) {
        event.preventDefault();
        resetSearch();
        searchToggle?.focus();
        return;
      }
      closeDrawer();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = panel.querySelectorAll('a, button, [tabindex]:not([tabindex="-1"])');
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const onPointerDown = (event) => {
    if (!isOpen || !isSearchOpen || !searchPanel) return;
    const target = event.target;
    if (target instanceof Node && !searchPanel.contains(target)) {
      resetSearch();
    }
  };

  if (panel.hidden === false) {
    setOpenVisualState();
  } else {
    setClosedVisualState();
  }

  let openSearch = null;
  if (searchToggle && searchPanel) {
    resetSearch();
    openSearch = () => {
      if (isSearchOpen) return;
      isSearchOpen = true;
      searchToggle.setAttribute('aria-expanded', 'true');
      searchTl?.kill();

      if (prefersReducedMotion) {
        gsap.set(searchToggle, { autoAlpha: 0, width: 0, pointerEvents: 'none' });
        gsap.set(searchPanel, { width: '100%', autoAlpha: 1, pointerEvents: 'auto' });
        searchInput?.focus();
        return;
      }

      searchTl = gsap.timeline({ defaults: { overwrite: 'auto' } });
      searchTl
        .to(searchToggle, {
          width: 0,
          autoAlpha: 0,
          duration: 0.22,
          ease: 'power2.out',
          pointerEvents: 'none'
        }, 0)
        .to(searchPanel, {
          width: '100%',
          autoAlpha: 1,
          duration: 0.32,
          ease: 'power2.out',
          pointerEvents: 'auto'
        }, 0.04)
        .call(() => searchInput?.focus());
    };

    searchToggle.addEventListener('click', openSearch);
  }

  openBtn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  openBtn.addEventListener('click', openDrawer);
  closeBtn.addEventListener('click', closeDrawer);
  backdrop.addEventListener('click', closeDrawer);
  closeLinks.forEach((link) => link.addEventListener('click', closeDrawer));
  window.addEventListener('keydown', onKeydown);
  document.addEventListener('pointerdown', onPointerDown);

  cleanupDrawerReveal = () => {
    openTl?.kill();
    closeTl?.kill();
    searchTl?.kill();
    openBtn.removeEventListener('click', openDrawer);
    closeBtn.removeEventListener('click', closeDrawer);
    backdrop.removeEventListener('click', closeDrawer);
    closeLinks.forEach((link) => link.removeEventListener('click', closeDrawer));
    if (openSearch) searchToggle?.removeEventListener('click', openSearch);
    window.removeEventListener('keydown', onKeydown);
    document.removeEventListener('pointerdown', onPointerDown);
    cleanupDrawerReveal = null;
  };
}


// ✅ Destroy before page transition
function destroy() {
  cleanupPlusCursor?.();
  cleanupDrawerReveal?.();
  textRevealSplits.forEach((split) => split?.revert?.());
  textRevealSplits = [];

  // Revert anything created in our root context first
  if (rootCtx) {
    rootCtx.revert();
    rootCtx = null;
  }

  // Then kill any remaining triggers (belt + braces)
  ScrollTrigger.getAll().forEach(trigger => trigger.kill());

  // Clear fade-up init flags so fresh page/state can re-register correctly
  document.querySelectorAll('.fade-up').forEach((el) => {
    if (el._fadeUpTrigger) {
      el._fadeUpTrigger.kill();
      el._fadeUpTrigger = null;
    }
    delete el.dataset.fadeUpInit;
  });

  // Revert any per-section GSAP contexts created for horizontal scroll
  document.querySelectorAll('.horizontal-scroll-section').forEach(section => {
    if (section._hScrollCtx) {
      section._hScrollCtx.revert();
      section._hScrollCtx = null;
    }
  });
}

// === Animation Functions ===
function animateAwardTables() {
  const tables = document.querySelectorAll('.award-table');
  let activeImage = null;
  
  // Offset from cursor (adjust to taste)
  const OFFSET_X = 20;
  const OFFSET_Y = 20;

  tables.forEach((el) => {
    const image = el.querySelector('img.award-img');
    if (!image) return;

    gsap.set(image, {
      position: 'fixed',
      left: 0,
      top: 0,
      xPercent: -50,
      yPercent: -50,
      autoAlpha: 0,
      willChange: 'transform, opacity',
      pointerEvents: 'none',
    });

    let setX, setY;
    
    const align = (e) => {
      setX?.(e.clientX + OFFSET_X);
      setY?.(e.clientY + OFFSET_Y);
    };

    const fade = gsap.to(image, {
      autoAlpha: 1,
      duration: 0.15,
      paused: true,
      onReverseComplete() {
        document.removeEventListener('mousemove', align);
      },
    });

    el.addEventListener('mouseenter', (e) => {
      const startX = activeImage && activeImage !== image
        ? gsap.getProperty(activeImage, 'x')
        : e.clientX + OFFSET_X;
      const startY = activeImage && activeImage !== image
        ? gsap.getProperty(activeImage, 'y')
        : e.clientY + OFFSET_Y;

      gsap.set(image, { x: startX, y: startY });
      activeImage = image;

      setX = gsap.quickTo(image, 'x', { duration: 0.25, ease: 'power3.out' });
      setY = gsap.quickTo(image, 'y', { duration: 0.25, ease: 'power3.out' });

      fade.play();
      document.addEventListener('mousemove', align);
      align(e);
    });

    el.addEventListener('mouseleave', () => fade.reverse());
  });
}


// ============================================
// ✅ SINGLE INITIALIZATION POINT
// ============================================

function initializeAnimations() {
  // If re-initialising (eg page transitions), tear down first
  destroy();

  rootCtx = gsap.context(() => {
    pinSections();
    ParallaxContent();
    createParallaxEffect();
    linkHoverEffect();
    fadeUpAnimation();
    drawerMenuRevealAnimation();
    animateAwardTables();
  });
}

// ============================================
// ✅ AUTO-INIT ON EVERY PAGE LOAD (DRY)
// ============================================
document.addEventListener('DOMContentLoaded', initializeAnimations);

// ✅ Export for modular use
export default {
  init: initializeAnimations,
  destroy,
  initializeAnimations
};
